import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import DeliveryEdit from '../components/DeliveryEdit';
import DeliveryDetailContainer from '../components/DeliveryDetailContainer';
import DeliveryChangeStatus from '../components/DeliveryChangeStatus';
import { Tab, Tabs } from 'react-bootstrap';
import { DeliveryShow, DeliveryStatus } from '../../../app/models/Delivery';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';

type RouterParams = {
    id: string;
};

const toDateInputValue = (d: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DeliveryEditPage = () => {
    const { id } = useParams<RouterParams>();
    const deliveryId = parseInt(id);
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { 
        showDelivery, 
        fetchingShowDelivery,
        editDelivery,
        fetchingEditDelivery,
        changeDeliveryStatus,
        fetchingChangeStatus
    } = useDeliveryService();

    const [delivery, setDelivery] = useState<DeliveryShow | undefined>(undefined);
    const [statuses, setStatuses] = useState<DeliveryStatus[]>([]);
    const [showDeliveredModal, setShowDeliveredModal] = useState(false);
    const [actualDeliveryDate, setActualDeliveryDate] = useState(() => toDateInputValue(new Date()));

    const breadcrumbs: BreadcrumbsItem[] = [
        {
            name: 'Entregas',
            url: '/deliveries',
            isActive: false
        },
        {
            name: `Editar Entrega #${id}`,
            url: `/deliveries/${id}/edit`,
            isActive: true
        }
    ];

    useEffect(() => {
        loadDelivery();
    }, [deliveryId]);

    const loadDelivery = () => {
        editDelivery(deliveryId, {
            onSuccess: (response: ServiceResponse) => {
                // Actualizar también con showDelivery para obtener todos los datos
                showDelivery(deliveryId, {
                    onSuccess: (showResponse: ServiceResponse) => {
                        setDelivery(showResponse.data.delivery);
                    },
                    onError: (showResponse: ServiceResponse) => {
                        toast.error(showResponse.message);
                    }
                });
                
                // Guardar los estados disponibles
                if (response.data.statuses) {
                    setStatuses(response.data.statuses);
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                navigationPage('/deliveries');
            }
        });
    };

    const doChangeStatus = (status: string, actualDeliveryDateIso?: string) => {
        if (showLoading) showLoading('loading', 'Cambiando estado...');
        const payload = status === 'DELIVERED' && actualDeliveryDateIso
            ? { actual_delivery_date: actualDeliveryDateIso }
            : undefined;
        changeDeliveryStatus(deliveryId, status, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setShowDeliveredModal(false);
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                toast.success(response.message, { autoClose: 2500 });
                loadDelivery();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setShowDeliveredModal(false);
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
            }
        }, payload);
    };

    const confirmDelivered = () => {
        const dateStr = actualDeliveryDate || toDateInputValue(new Date());
        doChangeStatus('DELIVERED', dateStr);
    };

    const requestChangeStatus = (status: string) => {
        if (status === 'DELIVERED') {
            setActualDeliveryDate(toDateInputValue(new Date()));
            setShowDeliveredModal(true);
            return;
        }
        const _text = `Está a punto de cambiar el estado de la entrega #${id}.`;
        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, cambiar',
                    onClick: () => doChangeStatus(status),
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La entrega no ha cambiado el estado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const formatDate = (d?: string | null) => {
        if (!d) return '';
        try {
            const date = new Date(d);
            return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString();
        } catch {
            return d as string;
        }
    };

    return (
        <>
            <Breadcrumbs pageSection={`Editar Entrega #${id}`} breadcrumbs={breadcrumbs} />

            <Tabs defaultActiveKey="general" id="delivery-edit-top-tabs" className="mb-3">
                <Tab eventKey="general" title="Datos Generales">
                    <div className="row">
                        <div className="col-12 col-lg-8">
                            <DefaultCard>
                                <h5 className="mb-3">Datos Generales</h5>
                                <DeliveryEdit
                                    deliveryId={deliveryId}
                                    onSaved={loadDelivery}
                                    isDelivered={delivery?.status === 'DELIVERED'}
                                />
                            </DefaultCard>
                        </div>

                        <div className="col-12 col-lg-4">
                            <DefaultCard>
                                <h5 className="mb-3">Datos del creador</h5>
                                {!fetchingShowDelivery && delivery && (
                                    <>
                                        <div className="mb-2">
                                            <label className="form-label">Creado por</label>
                                            <input
                                                className="form-control"
                                                value={
                                                    delivery.created_user
                                                        ? `${delivery.created_user.first_name} ${delivery.created_user.last_name}`
                                                        : ''
                                                }
                                                disabled
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label">Fecha de creación</label>
                                            <input
                                                className="form-control"
                                                value={formatDate(delivery.created_at)}
                                                disabled
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label">Editado por</label>
                                            <input
                                                className="form-control"
                                                value={
                                                    delivery.updated_user
                                                        ? `${delivery.updated_user.first_name} ${delivery.updated_user.last_name}`
                                                        : ''
                                                }
                                                disabled
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label">Fecha de actualización</label>
                                            <input
                                                className="form-control"
                                                value={formatDate(delivery.updated_at)}
                                                disabled
                                            />
                                        </div>
                                        <hr />
                                        <div className="mb-2">
                                            <label className="form-label">Activo</label>
                                            <input
                                                className="form-control"
                                                value={delivery.is_active ? 'Sí' : 'No'}
                                                disabled
                                            />
                                        </div>
                                    </>
                                )}
                            </DefaultCard>
                        </div>
                    </div>
                </Tab>
            </Tabs>

            {/* Cambio de Estado */}
            {delivery && statuses.length > 0 && (
                <DefaultCard>
                    <DeliveryChangeStatus
                        requestChangeStatus={requestChangeStatus}
                        fetchingChangeStatus={fetchingChangeStatus}
                        statuses={statuses}
                        status={delivery.status || 'PENDING'}
                    />
                </DefaultCard>
            )}

            {/* Detalles (Lotes) */}
            <DeliveryDetailContainer
                deliveryId={deliveryId}
                onUpdate={loadDelivery}
                isDelivered={delivery?.status === 'DELIVERED'}
            />

            {/* Modal fecha real de entrega al marcar Entregada */}
            {showDeliveredModal && (
                <>
                    <div className="modal-backdrop fade show" />
                    <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar entrega</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowDeliveredModal(false)} aria-label="Cerrar" />
                                </div>
                                <div className="modal-body">
                                    <p className="mb-2">Se registrará la fecha real de la entrega (día, mes y año). El sistema sugiere la fecha actual; puede ajustarla si lo desea.</p>
                                    <label className="form-label">Fecha real de entrega</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={actualDeliveryDate}
                                        onChange={(e) => setActualDeliveryDate(e.target.value)}
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowDeliveredModal(false)}>Cancelar</button>
                                    <button type="button" className="btn btn-success" onClick={confirmDelivered} disabled={fetchingChangeStatus}>Confirmar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default DeliveryEditPage;


